import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { CaretDown, Lightning, ArrowCounterClockwise } from '@phosphor-icons/react'
import {
  useBilling,
  creditsAsResults,
  creditTone,
  joinResults,
  TIER_CONFIG,
} from '../contexts/BillingContext'

/**
 * Contador de saldo permanente, con su panel.
 *
 * Vive en la barra del editor porque es donde se gasta. El número informa; el
 * panel traduce: nadie sabe lo que vale un crédito, todo el mundo sabe lo que
 * vale un guion.
 *
 * Controlado desde fuera para que un botón sin saldo pueda abrir el panel en
 * lugar de limitarse a no hacer nada.
 */

// Lo que se enseña en el desglose, en este orden. El precio no se escribe
// aquí: sale del backend, que es quien cobra.
const PRICED_ACTIONS = [
  'script_youtube_long',
  'script_youtube_medium',
  'script_short',
  'script_linkedin',
  'script_section',
]

function renewalDate(subscription) {
  const iso = subscription?.current_period_end
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}

export default function CreditsMeter({ open, onOpenChange }) {
  const { credits, creditPricing, creditWeights, hasByok, plan, subscription } = useBilling()
  const ref = useRef(null)

  const total = credits?.total ?? 0
  const tone = creditTone(total)

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = event => {
      if (ref.current && !ref.current.contains(event.target)) onOpenChange(false)
    }
    const onKeyDown = event => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onOpenChange])

  // Con clave propia no hay saldo que vigilar: el chip lo dice y se acaba ahí.
  if (hasByok) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 bg-ds-surface border border-ds-border-mid text-[#30D158] text-xs"
        style={{ borderRadius: 2, fontFamily: 'Barlow, sans-serif' }}
        data-testid="credits-meter-byok"
      >
        <Lightning size={13} weight="fill" />
        Tu API key
      </div>
    )
  }

  const monthly = TIER_CONFIG[plan]?.credits_per_month ?? 0
  // La barra nunca miente: si el saldo supera la asignación del plan (packs
  // comprados, créditos de alta), el listón sube en vez de desbordarse.
  const scale = Math.max(monthly, total, 1)
  const results = creditsAsResults(total, creditWeights)
  const renews = renewalDate(subscription)
  const priced = PRICED_ACTIONS.map(action =>
    creditPricing.find(item => item.action === action)
  ).filter(Boolean)

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        data-testid="credits-meter"
        aria-expanded={open}
        className="flex items-center gap-2 px-3 py-1.5 bg-ds-surface border border-ds-border-mid hover:border-[#3A3A3A] transition-colors"
        style={{ borderRadius: 2, fontFamily: 'Barlow, sans-serif' }}
      >
        <Lightning size={13} weight="fill" color={tone} />
        <span className="text-ds-text text-xs font-medium">
          {total} {total === 1 ? 'crédito' : 'créditos'}
        </span>
        <CaretDown size={11} color="#555555" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] max-w-[calc(100vw-2rem)] bg-ds-surface border border-ds-border-mid z-50"
          style={{ borderRadius: 2 }}
          data-testid="credits-panel"
        >
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="type-label text-ds-muted">Tu saldo</p>
              {renews && (
                <p className="text-[11px] text-[#555555]" style={{ fontFamily: 'Barlow' }}>
                  Se renueva el {renews}
                </p>
              )}
            </div>

            <div className="flex items-end gap-2">
              <span
                className="type-h2 text-ds-text text-[40px] leading-none"
                style={{ letterSpacing: '-0.02em' }}
              >
                {total}
              </span>
              {monthly > 0 && (
                <span
                  className="text-ds-muted text-[13px] pb-1"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  de {monthly} al mes en el plan {TIER_CONFIG[plan]?.label ?? 'Free'}
                </span>
              )}
            </div>

            <div className="flex h-1 bg-ds-border-mid overflow-hidden" style={{ borderRadius: 2 }}>
              <div
                className="h-full transition-all"
                style={{ width: `${Math.round((total / scale) * 100)}%`, background: tone }}
              />
            </div>

            <p
              className="text-ds-text text-[13px] leading-relaxed"
              style={{ fontFamily: 'Barlow, sans-serif' }}
            >
              {total > 0 && results.length > 0
                ? `Te quedan ${joinResults(results)}.`
                : 'Sin saldo. Editar y guardar a mano sigue siendo gratis.'}
            </p>
          </div>

          {priced.length > 0 && (
            <>
              <div className="ds-divider" />
              <div className="px-5 py-4 space-y-2.5">
                <p className="type-caption text-[#555555]">Qué consume créditos</p>
                {priced.map(item => (
                  <div key={item.action} className="flex items-center justify-between gap-4">
                    <span
                      className="text-ds-muted text-xs"
                      style={{ fontFamily: 'Barlow, sans-serif' }}
                    >
                      {item.label}
                    </span>
                    <span
                      className="text-ds-text text-xs font-medium shrink-0"
                      style={{ fontFamily: 'Barlow, sans-serif' }}
                    >
                      {item.credits}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4">
                  <span
                    className="text-ds-muted text-xs"
                    style={{ fontFamily: 'Barlow, sans-serif' }}
                  >
                    Editar a mano y guardar
                  </span>
                  <span
                    className="text-[#30D158] text-xs font-medium shrink-0"
                    style={{ fontFamily: 'Barlow, sans-serif' }}
                  >
                    Gratis
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="ds-divider" />
          <div className="flex items-start gap-3 px-5 py-4">
            <ArrowCounterClockwise size={14} color="#30D158" className="mt-0.5 shrink-0" />
            <p
              className="text-ds-muted text-xs leading-relaxed"
              style={{ fontFamily: 'Barlow, sans-serif' }}
            >
              Si una generación falla, el crédito vuelve solo. No tienes que pedirlo.
            </p>
          </div>

          <div className="px-5 pb-5">
            <Link
              to="/pricing"
              onClick={() => onOpenChange(false)}
              data-testid="credits-panel-upgrade"
              className="btn-accent w-full text-[12px] py-2.5 no-underline"
            >
              Conseguir más créditos
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
